// Wire models for the Issues sync feed, mirroring IssueSyncRow / IssueSyncResponse
// on the Web API. Modelled on what the endpoint actually returns, not on the
// server-side projection DTO: that DTO also carries `createDateTimeUtc`,
// `revision` and the full `groups` array, and the feed drops all three.
//
// Nothing here is optional. The API never returns null and never omits a field —
// an unset string arrives as "", an unset number as 0, and an unset date as the
// .NET minimum date rather than null. Marking these `| null` would describe a
// payload that does not exist and would push null checks through the whole engine.

import { IssueLifecycleState } from '../models/issue';
import { ContributionSourceType } from '../models/contribution';

// The projected row. One flat row per Issue, holding the vehicle facts as columns,
// so nothing here needs a join.
export interface IssueSyncRow {
  // The Issue id. The feed names it `id`; the row is filed under this value.
  id: string;
  // The cursor column. Ordered with the id as a tiebreak.
  updateDateTimeUtc: string;
  assetId: string;
  assetName: string;
  assetVin: string;
  make: string;
  model: string;
  // A string on the wire, not a number.
  modelYear: string;
  displayName: string;
  // Optional: a row cached before the field existed has none until the feed sends it again.
  description?: string;
  fingerprint: string;
  category: string;
  severity: number;
  confidence: number;
  // The feed serves open rows only. A row that closes arrives in `removedIds`
  // instead, so 'Closed' never appears here.
  lifecycleState: IssueLifecycleState;
  openedAtUtc: string;
  signalCount: number;
  reopenCount: number;
  origins: ContributionSourceType[];
  // An asset can belong to several location groups, so this is an array. "Issues at
  // my yard" reads it. Distinct from currentShopGroupId, which is where the asset
  // is right now — a filter that blurred the two would count assets passing through.
  homeLocationGroupIds: string[];
  // The shop-flagged geofence group the vehicle is inside, "" when none. An asset at
  // a customer site reads as empty, the same as an asset on the road.
  currentShopGroupId: string;
  vehicleSeverity: number;
  lastMovedAtUtc: string;
  caseId: string;
  // The tenant's own status id. 0 means the work has not started.
  caseStatus: number;
  lastCaseActivityUtc: string;
  assignedUserId: string;
  assignedLocationId: string;
  closedAtUtc: string;
  dispositionType: string;
}

export interface IssueSyncResponse {
  rows: IssueSyncRow[];
  removedIds: string[];
  // Opaque. Base64 of a timestamp and an id, and the only valid client behaviour is
  // to store it and send it back. On an empty page the server echoes the cursor it
  // was given, which parks the client where it already was.
  nextCursor: string;
  complete: boolean;
  scopeTotal: number;
  // True when scopeTotal hit the server's cap and is a floor rather than an exact
  // count. The residency decision must respect this: a floor means "at least".
  scopeTotalIsFloor: boolean;
  scopeVersion: string;
  asOfUtc: string;
}

export interface IssueSyncIdsResponse {
  ids: string[];
  scopeVersion: string;
  asOfUtc: string;
}

// The by-id read. Carries no cursor and must never advance one — it answers "what are
// these rows now", not "what changed since".
export interface IssueSyncRowsResponse {
  rows: IssueSyncRow[];
  // Requested ids that are no longer open and in scope. Closure and permission loss are
  // deliberately indistinguishable here; the client evicts either way.
  removedIds: string[];
  scopeVersion: string;
  asOfUtc: string;
}

// What one apply changed. Consumers patch from this instead of re-reading the store,
// so the cost of a change tracks the change rather than the size of the replica.
export interface IssueSyncDelta {
  upserted: IssueSyncRow[];
  removedIds: string[];
}

// What the client asks the server to sync. The server intersects the group list
// with the caller's permitted groups and can only narrow it, so this is a request
// and never a grant.
export interface IssueSyncScope {
  // Group ids. Empty means "whatever my permission allows".
  groupIds: string[];
  // Restricts the scope to one problem across the fleet. A scope dimension, not a
  // filter applied after the fact.
  fingerprint: string;
}

export function emptyScope(): IssueSyncScope {
  return { groupIds: [], fingerprint: '' };
}

// A stable key for a scope. Group ids are sorted so that two requests naming the
// same groups in a different order share one cursor instead of starting two syncs.
//
// The tenant is absent on purpose: each tenant has its own database, so a key never
// has to separate them. The permitted-group set is absent too — scopeVersion tracks
// that, and baking it into the key would abandon the cursor on every permission
// change instead of reconciling against it.
export function scopeKeyOf(scope: IssueSyncScope): string {
  const groups = [...scope.groupIds].filter(id => !!id).sort().join(',');
  return `g[${groups}]|f[${scope.fingerprint || ''}]`;
}

// The most rows the client will hold. Above this the list declines and asks for a
// narrower scope rather than showing a silently narrowed set. The server's own scope
// count is capped at twice this, so a count reported as a floor still lands above
// the threshold and the compare below stays meaningful.
export const RESIDENCY_LIMIT = 25_000;

// The server withholds rows whose timestamp is newer than this, so a delta cannot
// be assembled while writers with disagreeing clocks are still committing. A write
// therefore cannot appear in the feed sooner than this, whatever the projection
// costs. It mirrors SyncSafetyLagSeconds on the API: if that value moves, this one
// has to move with it, because nothing in the response reports it.
export const SERVER_SAFETY_LAG_MS = 30_000;

// Page size for the feed walk. The server clamps anything above 1000 silently and
// treats 0 or less as its own default, so staying inside the range keeps `complete`
// meaningful — it is computed against the clamped size, not the requested one.
export const SYNC_PAGE_LIMIT = 500;

// The most ids the by-id read accepts in one call. The server rejects more, so the
// caller chunks rather than discovering the bound as a failure.
export const ROWS_BY_ID_LIMIT = 200;

// How long a scope may go unreconciled. Reconciliation is what removes a row the
// user may no longer see, so this is a permission bound and not a freshness one.
export const RECONCILE_INTERVAL_MS = 30 * 60 * 1000;
